import { css } from '@emotion/css'
import { Select } from '@mantine/core'
import { IRRIGATION_ACTIVITY_FLOW_METER_VALUE_TYPE_PREFIX } from '@semios/app-platform-values-helpers-and-types'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

type SelectDataItem = { value: string; label: string }

export const DropdownSelectorPropertyFlowMeter = () => {
  const { selectedProperty } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedProperty: s.property,
  }))

  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const data = useMemo<SelectDataItem[]>(() => {
    return Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap((p) => {
        const propertyHasFlowMeter = Object.values(p.points || {}).some((point) =>
          point.valuesTimeseries.some((v) => v.includes(IRRIGATION_ACTIVITY_FLOW_METER_VALUE_TYPE_PREFIX)),
        )

        if (!propertyHasFlowMeter) {
          return []
        }

        return {
          value: String(p.propertyId),
          label: p.propertyName,
        }
      })
  }, [selectedProperty, properties])

  if (!data.length) return null

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        className={css({
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.grey500}`,
          color: colors.grey800,
          borderRadius: 3,
        })}
        clearable={false}
        data={data}
        filter={mantineSelectSearchFilterProp}
        onChange={(newProperty) => {
          if (newProperty === null) return

          setSelectedFieldAsset({ property: Number(newProperty) })
        }}
        placeholder={translate.phrases.banyanApp('Select a Property')}
        searchable
        size="xs"
        spellCheck="false"
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: { ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input, paddingRight: 28 },
        }}
        value={String(selectedProperty)}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
